import { useEffect, useState } from "react";
import {
  Paper,
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
  FormControlLabel,
  Alert,
  useTheme,
  alpha,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SecurityIcon from "@mui/icons-material/Security";
import ShieldIcon from "@mui/icons-material/Shield";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch, useSelector } from "react-redux";
import { useSnackbar } from "notistack";
import {
  fetchAclRules,
  createAclRule,
  updateAclRule,
  deleteAclRule,
} from "../features/firewall/sipAclSlice.js";
import ConfirmDialog from "./ConfirmDialog";

const EMPTY_FORM = {
  name: "",
  cidr: "",
  rule_type: "permit",
  direction: "both",
  description: "",
  active: true,
};

const DirectionLabel = ({ value }) => {
  const labels = { inbound: "Inbound", outbound: "Outbound", both: "Both" };
  const colors = { inbound: "info", outbound: "warning", both: "default" };
  return <Chip label={labels[value] ?? value} color={colors[value]} size="small" />;
};

const RuleTypeLabel = ({ value }) => {
  const theme = useTheme();
  const isPermit = value === "permit";
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.5,
        px: 1.5,
        py: 0.3,
        borderRadius: "12px",
        fontSize: "0.75rem",
        fontWeight: 600,
        backgroundColor: isPermit
          ? alpha(theme.palette.success.main, 0.12)
          : alpha(theme.palette.error.main, 0.12),
        color: isPermit ? theme.palette.success.main : theme.palette.error.main,
      }}
    >
      {isPermit ? <CheckCircleIcon sx={{ fontSize: 14 }} /> : <BlockIcon sx={{ fontSize: 14 }} />}
      {isPermit ? "Permit" : "Deny"}
    </Box>
  );
};

const RuleDialog = ({ open, onClose, initial, mode }) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.firewall);
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    setForm(initial ?? EMPTY_FORM);
  }, [initial, open]);

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const handleSave = async () => {
    try {
      if (mode === "edit") {
        await dispatch(updateAclRule({ id: form.id, ...form })).unwrap();
        enqueueSnackbar("Rule updated and applied to Asterisk", { variant: "success" });
      } else {
        await dispatch(createAclRule(form)).unwrap();
        enqueueSnackbar("Rule created and applied to Asterisk", { variant: "success" });
      }
      onClose();
    } catch (err) {
      enqueueSnackbar(err || "Failed to save rule", { variant: "error" });
    }
  };

  const isValid = form.name.trim() && form.cidr.trim();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ backgroundColor: "#1976d2", color: "#fff", mb: 1 }}
      >
        {mode === "edit" ? "Edit ACL Rule" : "New ACL Rule"}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        <Alert severity="info" sx={{ mb: 2, fontSize: "0.8rem" }}>
          Rules are written to <code>/etc/asterisk/mayday.d/sip_acl.conf</code> and
          applied immediately via PJSIP reload. Use CIDR notation, e.g.{" "}
          <strong>192.168.1.0/24</strong> or a single IP <strong>203.0.113.5</strong>.
        </Alert>
        <TextField
          label="Rule Name"
          name="name"
          value={form.name}
          onChange={handleChange}
          fullWidth
          margin="dense"
          placeholder="e.g. Office Network"
          required
        />
        <TextField
          label="IP / CIDR"
          name="cidr"
          value={form.cidr}
          onChange={handleChange}
          fullWidth
          margin="dense"
          placeholder="e.g. 192.168.1.0/24"
          required
          helperText="Single IP or CIDR range"
        />
        <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
          <FormControl fullWidth margin="dense">
            <InputLabel>Rule Type</InputLabel>
            <Select name="rule_type" value={form.rule_type} label="Rule Type" onChange={handleChange}>
              <MenuItem value="permit">Permit (Allow)</MenuItem>
              <MenuItem value="deny">Deny (Block)</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Direction</InputLabel>
            <Select name="direction" value={form.direction} label="Direction" onChange={handleChange}>
              <MenuItem value="both">Both</MenuItem>
              <MenuItem value="inbound">Inbound only</MenuItem>
              <MenuItem value="outbound">Outbound only</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <TextField
          label="Description (optional)"
          name="description"
          value={form.description}
          onChange={handleChange}
          fullWidth
          margin="dense"
          placeholder="e.g. Carrier SIP trunk IP range"
        />
        <FormControlLabel
          sx={{ mt: 1 }}
          control={
            <Switch
              name="active"
              checked={form.active}
              onChange={handleChange}
              color="success"
            />
          }
          label="Active"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !isValid}
        >
          {loading ? "Saving..." : "Save & Apply"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FirewallRules = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { rules = [], loading = false } = useSelector((state) => state.firewall ?? {});
  const { enqueueSnackbar } = useSnackbar();

  const [tab, setTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState("add");
  const [selected, setSelected] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  useEffect(() => {
    dispatch(fetchAclRules());
  }, [dispatch]);

  const tabRules =
    tab === 0
      ? rules
      : rules.filter(
          (r) =>
            r.direction === (tab === 1 ? "inbound" : "outbound") ||
            r.direction === "both"
        );

  const permitCount = tabRules.filter((r) => r.rule_type === "permit").length;
  const denyCount = tabRules.filter((r) => r.rule_type === "deny").length;

  const handleAdd = () => {
    setSelected(EMPTY_FORM);
    setDialogMode("add");
    setDialogOpen(true);
  };

  const handleEdit = (row) => {
    setSelected(row);
    setDialogMode("edit");
    setDialogOpen(true);
  };

  const handleDeleteClick = (row) => {
    setToDelete(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await dispatch(deleteAclRule(toDelete.id)).unwrap();
      enqueueSnackbar("Rule deleted and Asterisk updated", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err || "Failed to delete rule", { variant: "error" });
    } finally {
      setDeleteDialogOpen(false);
      setToDelete(null);
    }
  };

  const columns = [
    {
      field: "name",
      headerName: "Name",
      flex: 1,
      minWidth: 150,
    },
    {
      field: "cidr",
      headerName: "IP / CIDR",
      flex: 1,
      minWidth: 160,
      renderCell: (params) => (
        <Box
          component="code"
          sx={{
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
            px: 1,
            py: 0.25,
            borderRadius: 1,
            fontSize: "0.82rem",
            fontFamily: "monospace",
          }}
        >
          {params.value}
        </Box>
      ),
    },
    {
      field: "rule_type",
      headerName: "Type",
      width: 120,
      renderCell: (params) => <RuleTypeLabel value={params.value} />,
    },
    {
      field: "direction",
      headerName: "Direction",
      width: 130,
      renderCell: (params) => <DirectionLabel value={params.value} />,
    },
    {
      field: "description",
      headerName: "Description",
      flex: 1,
      minWidth: 180,
      renderCell: (params) => (
        <Typography variant="body2" color="text.secondary" noWrap>
          {params.value || "—"}
        </Typography>
      ),
    },
    {
      field: "active",
      headerName: "Status",
      width: 100,
      renderCell: (params) => (
        <Box
          sx={{
            px: 1.5,
            py: 0.3,
            borderRadius: "12px",
            fontSize: "0.75rem",
            fontWeight: 600,
            backgroundColor: params.value
              ? alpha(theme.palette.success.main, 0.12)
              : alpha(theme.palette.grey[500], 0.12),
            color: params.value
              ? theme.palette.success.main
              : theme.palette.text.disabled,
          }}
        >
          {params.value ? "Active" : "Inactive"}
        </Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.1) },
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              onClick={() => handleDeleteClick(params.row)}
              sx={{
                color: theme.palette.error.main,
                "&:hover": { backgroundColor: alpha(theme.palette.error.main, 0.1) },
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        borderRadius: 3,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
        background: theme.palette.background.paper,
      }}
    >
      {/* Header */}
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 3 }}>
        <Box>
          <Typography
            variant="h5"
            sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 600 }}
          >
            <SecurityIcon color="primary" sx={{ fontSize: 28 }} />
            SIP Firewall Rules
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Control which IP addresses can communicate with your SIP server.
            Changes are applied immediately to Asterisk.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{ borderRadius: 2, textTransform: "none", px: 3, flexShrink: 0 }}
        >
          Add Rule
        </Button>
      </Box>

      {/* Stats row */}
      <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.success.main, 0.08),
          }}
        >
          <ShieldIcon sx={{ color: theme.palette.success.main, fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
            {permitCount} Permit
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.error.main, 0.08),
          }}
        >
          <BlockIcon sx={{ color: theme.palette.error.main, fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.error.main }}>
            {denyCount} Deny
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.info.main, 0.08),
          }}
        >
          <SecurityIcon sx={{ color: theme.palette.info.main, fontSize: 18 }} />
          <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
            {tabRules.length} Total
          </Typography>
        </Box>
      </Box>

      {/* Tabs */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          mb: 2,
          "& .MuiTab-root": { textTransform: "none", fontWeight: 500 },
        }}
      >
        <Tab label="All Rules" />
        <Tab label="Inbound" />
        <Tab label="Outbound" />
      </Tabs>

      {/* Table */}
      <DataGrid
        rows={tabRules}
        columns={columns}
        pageSize={10}
        rowsPerPageOptions={[10, 25, 50]}
        autoHeight
        loading={loading}
        disableSelectionOnClick
        sx={{
          border: "none",
          "& .MuiDataGrid-cell": { borderColor: alpha(theme.palette.divider, 0.1) },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
            borderRadius: 2,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
          },
        }}
      />

      <RuleDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={selected}
        mode={dialogMode}
      />

      <ConfirmDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Firewall Rule"
        content={`Delete rule "${toDelete?.name}" (${toDelete?.cidr})? Asterisk will be updated immediately.`}
      />
    </Paper>
  );
};

export default FirewallRules;
